import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./Menu.css";
import "./MenuWorkflow.css";
import { fetchStoresAPI } from "../../utils/api/stores";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { useOperationalAccess } from "../../access/hooks/useOperationalAccess";
import SubscriptionLockNotice from "../subscription/SubscriptionLockNotice";
import { useRestaurantAccess } from "../../access/hooks/useRestaurantAccess";
import {
  ActionMenu,
  Archive,
  Button,
  ClipboardList,
  Input,
  Plus,
  Select,
  Upload,
  Copy,
  Tags,
  History,
  CircleX,
  Sparkles,
  Send,
} from "../ui";
import FoodMenu from "./foodMenu/FoodMenu";
import {
  createMenuAPI,
  duplicateMenuAPI,
  fetchMenuAPI,
  fetchMenusAPI,
  fetchRestaurantMenuHistoryAPI,
  fetchRestaurantMenuHistoryPageAPI,
  importMenuDraftAPI,
} from "../../utils/api/menu";
import { FEATURE_NAMES, isFeatureEnabled } from "../../utils/features";
import MenuHistoryModal from "./foodMenu/MenuHistoryModal/MenuHistoryModal";
import useMenuHistory, { MENU_HISTORY_PAGE_SIZE } from "./foodMenu/MenuHistoryModal/useMenuHistory";
import "./foodMenu/MenuHistoryModal/MenuHistoryModal.css";
import { menuActionsText } from "./menuActionsText";
import { readMenuFile, transformMenuData } from "../../utils/exportExcel";
import { LIFECYCLE_LABELS, menuErrorMessage, useMenuRole } from "./menuRole";
import { MenuOverviewSummary } from "./summary/MenuSummary";
import { summaryText } from "./summary/summaryText";
import { FILTER_COUNT_KEY, matchesMenuFilter } from "../../utils/menuHealth";
import MenuTopActions from "./topActions/MenuTopActions";
import { topActionsText } from "./topActions/topActionsText";
import { sendToAppText } from "./sendToApp/sendToAppText";
import { fetchAiImportsAPI } from "../../utils/api/menuAiImport";
import AiImportUploadDialog from "./aiImport/AiImportUploadDialog";
import AiImportDraftsPanel from "./aiImport/AiImportDraftsPanel";
import AiImportReview from "./aiImport/AiImportReview";
import { aiImportText } from "./aiImport/aiImportText";
import { ACTIVE_STATUSES } from "./aiImport/aiImportModel";
import MenuCopilot from "./copilot/MenuCopilot";
import AiWalletIndicator from "../billing/aiWallet/AiWalletIndicator";
import { notifyAiWalletChanged } from "../billing/aiWallet/aiWalletMenu";
import { copilotText } from "./copilot/copilotText";

// The open AI import review is kept in the URL (?aiImport=<id>) so a reload
// brings the reviewer back to the same draft.
const AI_IMPORT_PARAM = "aiImport";
const readAiImportParam = () =>
  new URLSearchParams(window.location.search).get(AI_IMPORT_PARAM) || "";
function writeAiImportParam(id) {
  const url = new URL(window.location.href);
  if (id) url.searchParams.set(AI_IMPORT_PARAM, id);
  else url.searchParams.delete(AI_IMPORT_PARAM);
  window.history.replaceState(window.history.state, "", url);
}

// Menus page for both sides: restaurant users see their restaurant(s); LUD
// staff see assigned (or all) restaurants. Changes save immediately.
export default function Menu() {
  const { lang, storeAccess, handleTokenExpiration } = useContext(Context);
  const isArabic = lang === "ar";
  const t = (ar, en) => (isArabic ? ar : en);
  const role = useMenuRole();
  const { can } = useAccess();

  const [restaurants, setRestaurants] = useState([]);
  const [menus, setMenus] = useState([]);
  const [restaurantId, setRestaurantId] = useState("");
  const [menuId, setMenuId] = useState("");
  const [menu, setMenu] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const importInputRef = useRef(null);
  const [createForm, setCreateForm] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [summaryStatus, setSummaryStatus] = useState("loading");
  // Menu tabs filter (lifecycle or "issues") and the item filter of the
  // selected menu; both can also be set from the summary cards.
  const [menuFilter, setMenuFilter] = useState("");
  const [itemFilter, setItemFilter] = useState("all");
  const [restaurantHistory, setRestaurantHistory] = useState({
    open: false,
    loading: false,
    list: [],
  });
  // MENU_HISTORY_PAGINATION: the restaurant history in pages with "load older" (off: the single request above).
  const pagedHistory = isFeatureEnabled(FEATURE_NAMES.MENU_HISTORY_PAGINATION);
  const pagedRestaurantHistory = useMenuHistory((cursor) =>
    fetchRestaurantMenuHistoryPageAPI(restaurantId, { limit: MENU_HISTORY_PAGE_SIZE, ...cursor }),
  );
  const closePagedRestaurantHistory = pagedRestaurantHistory.close;
  // Another restaurant never inherits the open history or an answer still on its way.
  useEffect(() => {
    closePagedRestaurantHistory();
  }, [restaurantId, closePagedRestaurantHistory]);
  // Feedback for the top Item / Section / Discount actions.
  const [notice, setNotice] = useState(null);
  // Send Items to App selection mode (cards get checkboxes; see FoodMenu).
  const [sendMode, setSendMode] = useState(false);
  // AI Menu Import: drafts of the open restaurant, upload dialog, review.
  const [aiDrafts, setAiDrafts] = useState([]);
  const [aiUploadOpen, setAiUploadOpen] = useState(false);
  const [aiReviewId, setAiReviewId] = useState(readAiImportParam);
  const aiText = aiImportText(lang);
  // Menu Copilot side panel for the open menu (same audience as AI import).
  const [copilotOpen, setCopilotOpen] = useState(false);

  // One request for every menu (archived included): the overview needs the
  // archived count, and "Show archived" then filters locally.
  const loadSummaries = useCallback(async () => {
    try {
      const list = await fetchMenusAPI({ includeArchived: true });
      setMenus(list);
      setSummaryStatus("ready");
      return list;
    } catch (err) {
      if (err.status === 401) handleTokenExpiration?.(); // 403 is a permission answer, not an expired session
      setError(menuErrorMessage(err, lang));
      setSummaryStatus("error");
      return [];
    }
  }, [handleTokenExpiration, lang]);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("auth-token");
      if (!token) return handleTokenExpiration?.();
      const [stores] = await Promise.all([
        fetchStoresAPI(token, storeAccess || []).catch(() => []),
        loadSummaries(),
      ]);
      setRestaurants(
        (stores || []).map((s) => ({
          _id: s._id,
          id: s.id,
          businessName: s.businessName,
        })),
      );
      setLoading(false);
      return undefined;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restaurants = accessible stores, plus any restaurant a menu belongs to.
  const restaurantOptions = useMemo(() => {
    const map = new Map(restaurants.map((r) => [String(r._id), r]));
    for (const m of menus)
      if (!map.has(String(m.restaurant._id)))
        map.set(String(m.restaurant._id), m.restaurant);
    return [...map.values()].sort((a, b) =>
      String(a.businessName).localeCompare(String(b.businessName)),
    );
  }, [restaurants, menus]);

  // Send to App, AI import and Copilot are checked against the selected
  // restaurant (user.between holds the public id): the right role is not
  // enough without access to that restaurant.
  const scope = {
    restaurantId: restaurantOptions.find(
      (r) => String(r._id) === String(restaurantId),
    )?.id,
  };
  const canUseAiImport = can("menu.ai.import", scope);
  const canUseCopilot = can("menu.copilot.use", scope);
  // Execution-time checks handed to the AI components (NA-010 / NA-011). A draft or a
  // menu is checked against ITS OWN restaurant, not the selected one: a draft opened
  // through the URL parameter can belong to another restaurant. `canFor` maps either id
  // form (`_id` or public id) to the public id with the stores list already loaded here;
  // an unknown restaurant is denied (ceo-level roles are global by design).
  const { canFor } = useRestaurantAccess(restaurantOptions);
  const aiImportAllowed = (restaurantRef) =>
    canFor("menu.ai.import", restaurantRef);
  const copilotAllowed = () =>
    Boolean(menu && String(menu._id) === String(menuId)) &&
    canFor("menu.copilot.use", menu.restaurant?._id ?? menu.restaurant?.id);
  // Sending items to an application is operational: the role must hold it AND the store's subscription must be active. A `new` client
  // (or a store whose subscription ended) sees the action locked and explained instead of missing.
  const operational = useOperationalAccess(scope.restaurantId);
  const sendAccess = operational.check("menu.send", scope);
  const canSendItems = sendAccess.allowed;
  // changing menus of the selected restaurant (visibility uses has() via useMenuRole)
  const canManageMenus = can("menu.manage", scope);
  const canManageAdmin = can("menu.manage.admin", scope);

  useEffect(() => {
    if (!restaurantId && restaurantOptions.length)
      setRestaurantId(String(restaurantOptions[0]._id));
  }, [restaurantId, restaurantOptions]);

  const allRestaurantMenus = useMemo(
    () =>
      menus.filter((m) => String(m.restaurant._id) === String(restaurantId)),
    [menus, restaurantId],
  );
  const restaurantMenus = useMemo(
    () =>
      allRestaurantMenus.filter(
        (m) =>
          (m.lifecycle !== "archived" ||
            showArchived ||
            menuFilter === "archived") &&
          matchesMenuFilter(menuFilter, m),
      ),
    [allRestaurantMenus, showArchived, menuFilter],
  );

  useEffect(() => {
    if (!restaurantMenus.some((m) => String(m._id) === String(menuId)))
      setMenuId(restaurantMenus[0]?._id || "");
  }, [restaurantMenus, menuId]);

  const loadMenu = useCallback(
    async (id) => {
      if (!id) return setMenu(null);
      try {
        setMenu(await fetchMenuAPI(id, { includeArchived: false }));
        setError("");
      } catch (err) {
        setMenu(null);
        setError(menuErrorMessage(err, lang));
      }
      return undefined;
    },
    [lang],
  );

  useEffect(() => {
    loadMenu(menuId);
  }, [menuId, loadMenu]);

  // Called by the editor after every write (fresh menu) or with null to reload.
  const handleMenuChange = async (next) => {
    // Archiving/restoring keeps the user on the same menu even if the tab
    // filters would now hide it (so Restore is one click away).
    if (next?.lifecycle === "archived" && !showArchived) setShowArchived(true);
    if (
      next &&
      ["active", "draft", "archived"].includes(menuFilter) &&
      next.lifecycle !== menuFilter
    )
      setMenuFilter("");
    if (next && String(next._id) === String(menuId)) setMenu(next);
    else await loadMenu(menuId);
    await loadSummaries();
  };

  // A top action saved: show the fresh menu when it is the open one, and
  // always refresh the summaries (counts, missing images, pricing issues...).
  const handleTopActionDone = async ({
    menuId: changedId,
    menu: fresh,
    message,
    type = "success",
  }) => {
    setError("");
    setNotice({
      type,
      text: message,
      menuId: String(changedId) === String(menuId) ? null : changedId,
    });
    if (String(changedId) === String(menuId)) {
      if (fresh && String(fresh._id) === String(menuId)) setMenu(fresh);
      else await loadMenu(menuId);
    }
    await loadSummaries();
  };

  const selectMenu = async (id) => {
    const list = await loadSummaries();
    const target = list.find((m) => String(m._id) === String(id));
    if (target) {
      setRestaurantId(String(target.restaurant._id));
      if (!matchesMenuFilter(menuFilter, target)) setMenuFilter("");
    }
    setMenuId(id);
  };

  const loadAiDrafts = useCallback(async () => {
    if (!restaurantId || !canUseAiImport) return setAiDrafts([]);
    try {
      const list = await fetchAiImportsAPI(restaurantId);
      setAiDrafts(Array.isArray(list) ? list : []);
    } catch (err) {
      setAiDrafts([]);
    }
    return undefined;
  }, [restaurantId, canUseAiImport]);

  useEffect(() => {
    loadAiDrafts();
  }, [loadAiDrafts]);

  // Refresh the list while the backend is still processing a draft.
  const aiProcessing = aiDrafts.some((d) => ACTIVE_STATUSES.includes(d.status));
  useEffect(() => {
    if (!aiProcessing) return undefined;
    const timer = setInterval(loadAiDrafts, 4000);
    return () => {
      clearInterval(timer);
      // Processing ended (charged, released or refused): re-read the AI Wallet.
      notifyAiWalletChanged();
    };
  }, [aiProcessing, loadAiDrafts]);

  const openAiReview = (id) => {
    setAiReviewId(String(id));
    writeAiImportParam(String(id));
  };
  const closeAiReview = () => {
    setAiReviewId("");
    writeAiImportParam("");
    loadAiDrafts();
  };

  const scrollToItems = () =>
    requestAnimationFrame(() =>
      document
        .getElementById("menu-items-toolbar")
        ?.scrollIntoView?.({ behavior: "smooth", block: "start" }),
    );

  // Restaurant-level item card: filter the selected menu, or open the first
  // menu in use that actually has matching items.
  const focusItems = (filter) => {
    const key = FILTER_COUNT_KEY[filter];
    const current = allRestaurantMenus.find(
      (m) => String(m._id) === String(menuId),
    );
    if (key && !(current?.counts?.[key] > 0)) {
      const target = allRestaurantMenus.find(
        (m) => m.lifecycle !== "archived" && m.counts?.[key] > 0,
      );
      if (target) {
        if (!matchesMenuFilter(menuFilter, target)) setMenuFilter("");
        setMenuId(String(target._id));
      }
    }
    setItemFilter(filter);
    scrollToItems();
  };

  const submitCreate = async () => {
    // handler-level authorization, against the selected restaurant
    if (!canManageMenus) return;
    setError("");
    try {
      let result;
      if (createForm.mode === "duplicate")
        result = await duplicateMenuAPI(
          createForm.sourceId,
          createForm.name ? { name: createForm.name } : {},
        );
      else if (createForm.mode === "import")
        result = await importMenuDraftAPI({
          restaurant: restaurantId,
          name: createForm.name,
          sections: createForm.sections,
        });
      else
        result = await createMenuAPI({
          restaurant: restaurantId,
          name: createForm.name,
        });
      setCreateForm(null);
      await selectMenu(result.menu._id);
    } catch (err) {
      setError(menuErrorMessage(err, lang));
    }
  };

  const onImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const { sections } = transformMenuData(await readMenuFile(file));
      setCreateForm({
        mode: "import",
        name: file.name.replace(/\.[^.]+$/, ""),
        sections,
      });
    } catch (err) {
      setError(t("تعذرت قراءة الملف", "Could not read the file"));
    }
  };

  // Copy Menu: any menu of the restaurant can be the source (the open one by default).
  const duplicateSource = allRestaurantMenus.find(
    (m) => String(m._id) === String(createForm?.sourceId),
  );
  const actionsText = menuActionsText(lang);
  // Everything recorded for the restaurant, including addition changes that
  // belong to no single menu.
  const openRestaurantHistory = async () => {
    if (pagedHistory) return pagedRestaurantHistory.openHistory();
    setRestaurantHistory({ open: true, loading: true, list: [] });
    try {
      setRestaurantHistory({
        open: true,
        loading: false,
        list: await fetchRestaurantMenuHistoryAPI(restaurantId),
      });
    } catch (err) {
      setRestaurantHistory({ open: false, loading: false, list: [] });
      setError(menuErrorMessage(err, lang));
    }
  };
  // Top toolbar: Menu | Item | Section | Discount | AI. Each entry keeps the
  // visibility rule and handler of the standalone button it replaced.
  const topText = topActionsText(lang);
  const canCreateMenu = canManageMenus && Boolean(restaurantId);
  const menuGroupItems = [
    {
      key: "new",
      label: t("قائمة طعام جديدة", "New menu"),
      icon: <Plus size={16} />,
      hidden: !canCreateMenu,
      onClick: () => setCreateForm({ mode: "scratch", name: "" }),
    },
    {
      key: "duplicate",
      label: t("نسخ قائمة طعام موجودة", "Duplicate a menu"),
      icon: <Copy size={16} />,
      hidden: !canCreateMenu || allRestaurantMenus.length === 0,
      onClick: () =>
        setCreateForm({
          mode: "duplicate",
          sourceId: String(menuId || allRestaurantMenus[0]._id),
          name: "",
        }),
    },
    {
      key: "import",
      label: t("استيراد", "Import"),
      icon: <Upload size={16} />,
      hidden: !canCreateMenu,
      onClick: () => importInputRef.current?.click(),
    },
    {
      key: "history",
      label: actionsText.restaurantHistory,
      icon: <History size={16} />,
      hidden: !restaurantId,
      onClick: openRestaurantHistory,
    },
    {
      key: "archived",
      label: t("إظهار المؤرشفة", "Show archived"),
      icon: <Archive size={16} />,
      checked: showArchived,
      divider: true,
      onClick: () => setShowArchived((shown) => !shown),
    },
  ];
  const aiGroupItems = [
    {
      key: "import",
      label: aiText.action,
      icon: <Sparkles size={16} />,
      hidden: !(canCreateMenu && canUseAiImport),
      onClick: () => canUseAiImport && setAiUploadOpen(true),
    },
    {
      key: "copilot",
      label: copilotText(lang).open,
      icon: <Sparkles size={16} />,
      hidden: !(canUseCopilot && menuId),
      checked: copilotOpen,
      onClick: () => canUseCopilot && setCopilotOpen((open) => !open),
    },
  ];
  const text = summaryText(lang);
  // Send Items to App: LUD staff who can change menus (the backend enforces the same rule).
  const sendText = sendToAppText(lang);
  const sendModeAvailable =
    canSendItems && Boolean(menu) && menu.lifecycle !== "archived";
  const menuFilterLabel =
    menuFilter === "issues"
      ? text.menusWithIssues
      : LIFECYCLE_LABELS[lang]?.[menuFilter];

  return (
    <div
      className={`menu-container menu-page ${copilotOpen && menuId ? "has-copilot" : ""}`}
      dir={isArabic ? "rtl" : "ltr"}
    >
      <div className="menu-header-row menu-page-header">
        {restaurantOptions.length > 1 && (
          <Select
            value={restaurantId}
            placeholder={t("اختر المطعم", "Select restaurant")}
            searchable
            fullWidth={false}
            options={restaurantOptions.map((r) => ({
              value: String(r._id),
              label: r.businessName,
            }))}
            onChange={(e) => {
              setRestaurantId(e.target.value);
              setMenuId("");
              setMenuFilter("");
            }}
          />
        )}
        {restaurantOptions.length === 1 && (
          <h2 className="menu-page-title">
            {restaurantOptions[0].businessName}
          </h2>
        )}

        {sendAccess.locked && (
          <SubscriptionLockNotice
            lang={lang}
            reason={sendAccess.reason}
            title={isArabic ? "إرسال القائمة للتطبيقات" : "Sending the menu to applications"}
          />
        )}

        <div className="menu-top-toolbar">
          {(canSendItems || sendAccess.locked) && (
            <Button
              type="button"
              variant="primary"
              appearance={sendMode && sendModeAvailable ? "filled" : "outline"}
              className={`send-mode-toggle ${sendMode && sendModeAvailable ? "is-active" : ""}`}
              icon={<Send size={16} />}
              aria-pressed={sendMode && sendModeAvailable}
              disabled={sendAccess.locked || (!sendModeAvailable && !sendMode)}
              title={sendAccess.locked ? (isArabic ? "يتطلب اشتراكًا فعّالًا" : "Needs an active subscription") : sendMode ? sendText.modeOn : undefined}
              onClick={() => setSendMode((on) => canSendItems && !on)}
            >
              {sendText.action}
            </Button>
          )}
          <ActionMenu
            label={topText.menusGroup}
            triggerLabel={topText.menus}
            icon={<ClipboardList size={16} />}
            items={menuGroupItems}
            buttonClassName="menu-top-action"
          />
          {canManageMenus && restaurantId && (
            <MenuTopActions
              menus={allRestaurantMenus}
              restaurant={restaurantOptions.find(
                (r) => String(r._id) === String(restaurantId),
              )}
              currentMenuId={menuId}
              onDone={handleTopActionDone}
              disabled={loading}
            />
          )}
          <ActionMenu
            label={topText.aiGroup}
            triggerLabel={topText.ai}
            icon={<Sparkles size={16} />}
            items={aiGroupItems}
            className="menu-top-ai"
            buttonClassName="menu-top-action"
          />
          {/* AI Wallet balance of the restaurant (only for users who may see it) -> Billing to recharge. */}
          {canUseAiImport && restaurantId && (
            <AiWalletIndicator
              isMenu={true}
              restaurant={restaurantId}
              lang={isArabic ? "ar" : "en"}
            />
          )}
          {canCreateMenu && (
            <input
              ref={importInputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={onImportFile}
              aria-label={t(
                "استيراد ملف كمسودة جديدة",
                "Import a file as a new draft",
              )}
            />
          )}
        </div>
      </div>

      {error && (
        <p className="menu-error" role="alert">
          {error}
        </p>
      )}

      {notice && (
        <div
          className={`menu-notice menu-notice-${notice.type}`}
          role={notice.type === "error" ? "alert" : "status"}
        >
          <span>{notice.text}</span>
          <span className="menu-notice-actions">
            {notice.menuId && (
              <Button
                type="button"
                variant="primary"
                appearance="soft"
                size="xs"
                rounded
                onClick={() => {
                  selectMenu(notice.menuId);
                  setNotice(null);
                }}
              >
                {t("فتح قائمة الطعام", "Open menu")}
              </Button>
            )}
            <Button
              type="button"
              variant="warning"
              appearance="soft"
              size="xs"
              rounded
              icon={<CircleX size={14} />}
              aria-label={t("إغلاق", "Dismiss")}
              onClick={() => setNotice(null)}
            />
          </span>
        </div>
      )}

      {canUseAiImport && (
        <AiImportDraftsPanel
          authorize={() => aiImportAllowed(restaurantId)}
          drafts={aiDrafts}
          canWrite={canManageMenus}
          onOpen={openAiReview}
          onChanged={loadAiDrafts}
          onOpenMenu={(id) => selectMenu(id)}
          onError={(message) => setNotice({ type: "error", text: message })}
        />
      )}

      {canManageAdmin && (
        <MenuOverviewSummary
          menus={allRestaurantMenus}
          status={loading ? "loading" : summaryStatus}
          onRetry={() => {
            setSummaryStatus("loading"); // show the loading state, never stale zeros, while the retry runs
            loadSummaries();
          }}
          menuFilter={menuFilter}
          onMenuFilter={setMenuFilter}
          itemFilter={itemFilter}
          onItemFilter={focusItems}
          isBranchUser={role.isBranchUser}
        />
      )}

      {menuFilter && (
        <div className="menu-list-filter">
          <span>{text.filteredBy}</span>
          <Tags
            variant="primary"
            size="sm"
            closable
            closeLabel={text.clearFilter}
            onClose={() => setMenuFilter("")}
          >
            {menuFilterLabel}
          </Tags>
        </div>
      )}

      {loading ? (
        <p>{t("جاري التحميل...", "Loading...")}</p>
      ) : allRestaurantMenus.length === 0 ? (
        <div className="food-empty">
          <p>
            {t(
              "لا توجد قوائم طعام لهذا المطعم بعد. أنشئ قائمة طعام جديدة أو استورد ملفاً.",
              "No menus for this restaurant yet. Create one or import a file.",
            )}
          </p>
        </div>
      ) : restaurantMenus.length === 0 ? (
        <div className="food-empty">
          <p>
            {menuFilter
              ? text.noMenusMatch
              : t(
                  "كل قوائم الطعام لهذا المطعم مؤرشفة.",
                  "All menus of this restaurant are archived.",
                )}
          </p>
          <Button
            type="button"
            variant="primary"
            appearance="soft"
            size="sm"
            rounded
            onClick={() =>
              menuFilter ? setMenuFilter("") : setShowArchived(true)
            }
          >
            {menuFilter
              ? text.clearFilter
              : t("إظهار المؤرشفة", "Show archived")}
          </Button>
        </div>
      ) : menu ? (
        <FoodMenu
          menu={menu}
          menus={restaurantMenus}
          allMenus={menus}
          restaurants={restaurantOptions}
          onSelectMenu={selectMenu}
          onMenuChange={handleMenuChange}
          sendMode={sendMode}
          onSendModeChange={(next) =>
            setSendMode(Boolean(next) && canSendItems)
          }
          canSend={canSendItems}
          filter={itemFilter}
          onFilterChange={setItemFilter}
        />
      ) : null}

      {aiUploadOpen && canUseAiImport && (
        <AiImportUploadDialog
          authorize={() => aiImportAllowed(restaurantId)}
          restaurantId={restaurantId}
          menus={allRestaurantMenus}
          initialMenuId={menuId}
          onClose={() => setAiUploadOpen(false)}
          onUploaded={(draft, duplicate) => {
            setAiUploadOpen(false);
            if (duplicate)
              setNotice({ type: "success", text: aiText.alreadyUploaded });
            loadAiDrafts();
            openAiReview(draft._id);
          }}
        />
      )}

      {aiReviewId && canUseAiImport && (
        <AiImportReview
          authorize={aiImportAllowed}
          draftId={aiReviewId}
          menus={allRestaurantMenus}
          canWrite={canManageMenus}
          onClose={closeAiReview}
          onChanged={loadAiDrafts}
          onApproved={async (res) => {
            // Approve All reports entries approved / rejected; a plain approval its new items.
            const bulk = res.result?.bulk?.entries;
            setNotice({
              type: "success",
              text: bulk
                ? aiText.approveAll.done(bulk.approve, bulk.rejected)
                : aiText.approvedDone(res.result?.createdItems ?? 0),
            });
            closeAiReview();
            if (res.result?.menuId) await selectMenu(res.result.menuId);
          }}
        />
      )}

      {copilotOpen && canUseCopilot && menuId && (
        <MenuCopilot
          authorize={copilotAllowed}
          menuId={menuId}
          menuName={menu?.name || ""}
          canWrite={canManageMenus}
          onClose={() => setCopilotOpen(false)}
          onApplied={async () => {
            await loadMenu(menuId);
            await loadSummaries();
          }}
        />
      )}

      {pagedHistory ? (
        <MenuHistoryModal
          open={pagedRestaurantHistory.open}
          history={pagedRestaurantHistory.list}
          loading={pagedRestaurantHistory.loading}
          loadingMore={pagedRestaurantHistory.loadingMore}
          hasMore={pagedRestaurantHistory.hasMore}
          error={pagedRestaurantHistory.error}
          moreError={pagedRestaurantHistory.moreError}
          onLoadMore={pagedRestaurantHistory.loadMore}
          onRetry={pagedRestaurantHistory.retry}
          onClose={pagedRestaurantHistory.close}
        />
      ) : (
        <MenuHistoryModal
          open={restaurantHistory.open}
          history={restaurantHistory.list}
          loading={restaurantHistory.loading}
          onClose={() =>
            setRestaurantHistory({ open: false, loading: false, list: [] })
          }
        />
      )}

      {createForm && (
        <div className="food-modal-overlay">
          <div
            className="food-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t("قائمة طعام جديدة", "New menu")}
          >
            <h3>
              {createForm.mode === "duplicate"
                ? t("نسخ قائمة طعام موجودة", "Duplicate a menu")
                : createForm.mode === "import"
                  ? t("مسودة جديدة من ملف", "New draft from file")
                  : t("قائمة طعام جديدة من الصفر", "New menu from scratch")}
            </h3>
            {createForm.mode === "duplicate" ? (
              <>
                <Select
                  label={t("قائمة الطعام المصدر", "Source menu")}
                  value={createForm.sourceId}
                  options={allRestaurantMenus.map((m) => ({
                    value: String(m._id),
                    label: `${m.name} · ${LIFECYCLE_LABELS[lang]?.[m.lifecycle] || m.lifecycle}`,
                  }))}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      sourceId: e.target.value,
                    }))
                  }
                />
                <p className="menu-note">
                  {t(
                    "النسخة مستقلة ولا تتأثر بتعديلات الأصل.",
                    "The copy is independent and does not follow later changes to the original.",
                  )}{" "}
                  {duplicateSource && duplicateSource.lifecycle !== "active"
                    ? t(
                        "المصدر غير مفعّل، لذلك ستُنشأ النسخة كمسودة.",
                        "The source is not active, so the copy is created as a draft.",
                      )
                    : t("ستكون النسخة مفعّلة.", "The copy will be active.")}
                </p>
              </>
            ) : (
              <p className="menu-note">
                {t(
                  "ستُنشأ كمسودة. فعّلها عند الانتهاء لربطها بالفروع.",
                  "Created as a draft. Activate it when ready to assign it to branches.",
                )}
              </p>
            )}
            <Input
              label={t("اسم قائمة الطعام", "Menu name")}
              placeholder={
                createForm.mode === "duplicate"
                  ? t(
                      "اتركه فارغاً لاسم تلقائي مثل v2",
                      "Leave empty for an automatic name like v2",
                    )
                  : ""
              }
              value={createForm.name}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />
            {createForm.mode === "import" && (
              <p className="menu-note">
                {createForm.sections.length} {t("قسم", "sections")} ·{" "}
                {createForm.sections.reduce((n, s) => n + s.items.length, 0)}{" "}
                {t("صنف", "items")}
              </p>
            )}
            <div className="food-modal-actions">
              <Button
                type="button"
                variant="primary"
                appearance="filled"
                disabled={
                  createForm.mode !== "duplicate" && !createForm.name.trim()
                }
                onClick={submitCreate}
              >
                {t("إنشاء", "Create")}
              </Button>
              <Button
                type="button"
                variant="warning"
                appearance="soft"
                onClick={() => setCreateForm(null)}
              >
                {t("إلغاء", "Cancel")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
